import { createSlice } from '@reduxjs/toolkit';
import { calculateBoundingBoxFromCoords, flyToWithOffset } from '../common/utils/helperFunctions';

const initialState =  {
    collectionsView: false,
    isSidebarOpen: false,
    searchView: false,
    exportsView: false,
    multiSearchView: false,
    analysisMode: false,
    displayView: 'explorerView',
    mode: 'simple_select',
    coordinates: [],
    bbox: '',
    geometry: {},
    filterOption: '',
    searchedAoi: {},
    multicoordinates: {},
    highlightName: '',
    orderPreview: {},
    isOrdersRoute: false,
};

const mapSlice = createSlice({
    name: 'mapSlice',
    initialState,
    reducers: {
        setMapRef: (state, action) => {
            state.mapRef = action.payload;
        },
        setDrawRef: (state, action) => {
            state.drawRef = action.payload;
        },
        cleanUpDraw: (state) => {
            if (state.drawRef) {
                state.drawRef.deleteAll(); 
                state.drawRef.changeMode('simple_select');
            }
        },
        drawAOI: (state, action) => {
            if (state.drawRef) {
                state.drawRef.deleteAll();
                state.drawRef.changeMode(action.payload);
            }
        },
        drawMultiAOI: (state, action) => {
            if (state.drawRef) {
                state.drawRef.changeMode(action.payload);
            }
        },
        drawEditModeAOI: (state, action) => {
            if (state.drawRef) {
                const shapeId = state.drawRef.add({
                    type: 'Feature',
                    geometry: action.payload,
                });
                if(action.payload && action.payload.type === 'Point'){
                    state.drawRef.changeMode('simple_select', { featureId: shapeId[0] });
                } else {
                    state.drawRef.changeMode('direct_select', { featureId: shapeId[0] });
                }
            }
        },
        zoomIn: (state) => {
            if (state.mapRef) {
                state.mapRef.zoomIn();
            }
        },
        zoomOut: (state) => {
            if (state.mapRef) {
                state.mapRef.zoomOut();
            }
        },
        setViewport: (state, action) => {
            if (state.mapRef) {
                state.mapRef.fitBounds(action.payload, {
                    padding: {top: 200, bottom: 200, left: 400, right: 200}
                });
            }
        },
        setPosition: (state, action) => {
            if (!state.mapRef) return;
        
            const { position, offset, zoom } = action.payload;
        
            flyToWithOffset(
                state.mapRef,
                position,
                offset,           
                zoom !== undefined ? { zoom } : {}
            );
        },
        showSidebarRight: (state) => {
            state.isSidebarOpen = true;
        },
        hideSidebarRight: (state) => {
            state.isSidebarOpen = false;
        },
        showCollectionsView: (state) => {
            state.collectionsView = true;
        },
        hideCollectionsView: (state) => {
            state.collectionsView = false;
        },
        setDisplayView: (state, action) => {
            state.displayView = action.payload;
        },
        resetDisplayView: (state) => {
            state.displayView = 'explorerView';
        },
        setSearchView: (state) => {
            state.searchView = true;
        },
        resetSearchView: (state) => {
            state.searchView = false;
        },
        setExportsView: (state) => {
            state.exportsView = true;
        },
        resetExportsView: (state) => {
            state.exportsView = false;
        },
        setMode: (state, action) => {
            state.mode = action.payload;
        },
        resetMode: (state) => {
            state.mode = 'simple_select';
        },
        setAOICoordinates: (state, action) => {
            let coordinates = [];
            if (state.geometry?.type === "MultiPolygon") {
                state.coordinates = action.payload;          // keep full multipolygon structure
                const flattened = action.payload.flat(2);    // flatten to [lng, lat]
                state.bbox = calculateBoundingBoxFromCoords(flattened);
                return;
            }else if (action.payload.length === 2) {
                coordinates.push(action.payload[0]);
                coordinates.push(action.payload[1]);
                state.coordinates = [...state.coordinates, coordinates];
            } else {
                coordinates = action.payload[0];
                state.coordinates = coordinates;
                state.bbox = calculateBoundingBoxFromCoords(coordinates);
            }
        },
        setMultiAOICoordinates: (state, action) => {
            state.multicoordinates = action.payload;
        },
        resetCoordinatesGeometry: (state) => {
            state.coordinates = [];
            state.geometry = {};
            state.bbox = '';
        },
        resetMultiAOICoordinates: (state) => {
            state.multicoordinates = {};
        },
        setGeometryDrawn: (state, action) => {
            state.geometry = action.payload;
        },
        resetGeometryDrawn: (state) => {
            state.geometry = {};
        },
        resetMapSlice: (state) => {
            // isOrdersRoute is route-lifetime state, not view state - this reducer
            // fires repeatedly while still mounted on OrdersView (filter changes,
            // toggling to list view), so a blanket reset would flip it off early.
            return { ...initialState, isOrdersRoute: state.isOrdersRoute };
        },
        setFilterOption: (state, action) => {
            state.filterOption = action.payload;
        },
        resetFilterOption: (state) => {
            state.filterOption = '';
        },
        setSearchedAoi: (state, action) => {
            state.searchedAoi = action.payload;
        },
        resetSearchedAoi: (state) => {
            state.searchedAoi = {};
        },
        setAnalysisMode: (state) => {
            state.analysisMode = true;
        },
        resetAnalysisMode: (state) => {
            state.analysisMode = false;
        },
        setMultiSearchView: (state) => {
            state.multiSearchView = true;
        },
        resetMultiSearchView: (state) => {
            state.multiSearchView = false;
        },
        setHighlightAOI: (state, action) => {
            state.highlightName = action.payload;
        },
        resetHighlightAOI: (state) => {
            state.highlightName = '';
        },
        setOrderAoi: (state, action) => {
            state.orderPreview = action.payload;
        },
        resetOrderAoi: (state) => {
            state.orderPreview = {};
        },
        setOrdersRoute: (state) => {
            state.isOrdersRoute = true;
        },
        resetOrdersRoute: (state) => {
            state.isOrdersRoute = false;
        },
    },
});

export const {
    setMapRef,
    setDrawRef,
    cleanUpDraw,
    drawAOI,
    drawMultiAOI,
    drawEditModeAOI,
    zoomIn,
    zoomOut,
    setViewport,
    setPosition,
    showSidebarRight,
    hideSidebarRight,
    showCollectionsView,
    hideCollectionsView,
    setDisplayView,
    resetDisplayView,
    setSearchView,
    resetSearchView,
    setExportsView,
    resetExportsView,
    setMode,
    resetMode,
    setAOICoordinates,
    setMultiAOICoordinates,
    resetCoordinatesGeometry,
    resetMultiAOICoordinates,
    setGeometryDrawn,
    resetGeometryDrawn,
    resetMapSlice,
    setFilterOption,
    resetFilterOption,
    setSearchedAoi,
    resetSearchedAoi,
    setAnalysisMode,
    resetAnalysisMode,
    setMultiSearchView,
    resetMultiSearchView,
    setHighlightAOI,
    resetHighlightAOI,
    setOrderAoi,
    resetOrderAoi,
    setOrdersRoute,
    resetOrdersRoute
} = mapSlice.actions;

export default mapSlice.reducer;

// thunk function to ensure that drawing mode is set first to allow drawing
export const selectMode = (mode, callback) => async (dispatch, getState) => {

    await dispatch(setMode(mode));

    if (typeof callback === 'function') {
        callback(getState(), mode);
    }
};

// thunk function to open exports view only when search view is not opened
export const showExports = () => async (dispatch, getState) => {
    const state = getState();
    if(!state.map.searchView) {
        await dispatch(setExportsView());
    };
};